import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AppLayout } from "@/components/AppLayout";
import { RoleGuard } from "@/components/RoleGuard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { CardDestaque, COR_STATUS, PageHeader, SecaoCard } from "@/components/padroes";
import {
  ESTILO_PARCELA,
  estadoParcela,
  mesCurto,
  parcelaAtual,
  regraCurta,
  resumirPrograma,
  statusPrograma,
  tituloBonificacao,
} from "@/components/bonificacao/programa";
import { useAuth } from "@/context/AuthContext";
import { useNomeBonificacao } from "@/hooks/useNomeBonificacao";
import { api } from "@/lib/api";
import { formatCents, formatDate, formatMesRef, maskReaisInput, parseReaisInput } from "@/lib/format";
import { cn } from "@/lib/utils";
import { formatDataCivil } from "@/data/beneficios";
import { descreverRegras, type Bonificacao, type ConfigBonificacao, type TipoCalculoBonificacao } from "@/data/bonificacao";
import { toast } from "sonner";
import { ChevronRight, Loader2, Plus } from "lucide-react";

const somarMeses = (competencia: string, meses: number) => {
  const [ano, mes] = competencia.split("-").map(Number);
  const total = ano * 12 + (mes - 1) + meses;
  return `${Math.floor(total / 12)}-${String((total % 12) + 1).padStart(2, "0")}`;
};

/** Último dia do mês anterior à competência — data de corte sugerida do adiantamento. */
const fimDoMesAnterior = (competencia: string) => {
  const [ano, mes] = competencia.split("-").map(Number);
  if (!ano || !mes) return "";
  return new Date(Date.UTC(ano, mes - 1, 0)).toISOString().slice(0, 10);
};

const proximoMes = () => {
  const hoje = new Date();
  return somarMeses(`${hoje.getFullYear()}-${String(hoje.getMonth() + 1).padStart(2, "0")}`, 1);
};

/** Card destaque de uma bonificação com parcela aberta, com a barra das parcelas. */
const CardPrograma = ({ b, nome }: { b: Bonificacao; nome: string }) => {
  const status = statusPrograma(b.competencias);
  const atual = parcelaAtual(b.competencias);
  const r = resumirPrograma(b.competencias);
  const n = b.competencias.length;
  const colunas = { gridTemplateColumns: `repeat(${n}, minmax(0, 1fr))` };
  const rotulo =
    status === "em_pagamento"
      ? `Em pagamento · parcela ${atual?.parcela}/${n} em rascunho`
      : `Em preparação · parcela 1/${n} em rascunho`;

  return (
    <CardDestaque
      to={`/rh/bonificacao/${(atual ?? b.competencias[0]).id}`}
      corStatus={status === "em_pagamento" ? COR_STATUS.ambar : COR_STATUS.cinza}
      rotulo={rotulo}
      titulo={
        <>
          {tituloBonificacao(b, nome)}
          <span className="mt-1 block text-[13px] font-normal tracking-normal text-muted-foreground">
            {regraCurta(b)}
            {b.adiantamento && ` · dias contados até ${formatDataCivil(b.dataBase)}`}
          </span>
        </>
      }
      acao="Abrir bonificação"
      metricas={[
        { rotulo: "Colaboradores", valor: r.colaboradores },
        { rotulo: "Total do programa", valor: formatCents(r.total) },
        { rotulo: "Já pago", valor: <span className="text-primary-text">{formatCents(r.pago)}</span> },
      ]}
    >
      <div className="flex flex-col gap-2">
        <div className="grid gap-1" style={colunas}>
          {b.competencias.map((c) => (
            <span key={c.id} className={cn("h-1.5 rounded-full", ESTILO_PARCELA[estadoParcela(c, atual)].barra)} />
          ))}
        </div>
        <div className="grid gap-1 text-xs text-muted-foreground" style={colunas}>
          {b.competencias.map((c) => {
            const estado = estadoParcela(c, atual);
            return (
              <span key={c.id} className="truncate" title={formatMesRef(c.competencia)}>
                {c.parcela}/{n} · {mesCurto(c.competencia)} · {estado}
              </span>
            );
          })}
        </div>
      </div>
    </CardDestaque>
  );
};

const RHBonificacoes = () => {
  const { user } = useAuth();
  const nav = useNavigate();
  const nome = useNomeBonificacao();
  const ehRH = user?.role === "rh";
  // Lista inteira (sem `page`): os programas em andamento viram cards e os concluídos, a tabela.
  const [items, setItems] = useState<Bonificacao[]>([]);
  const [carregando, setCarregando] = useState(true);

  const load = useCallback(async () => {
    try {
      setItems(await api.get<Bonificacao[]>("/bonificacoes"));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Erro ao carregar a lista");
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const [novaOpen, setNovaOpen] = useState(false);
  const [config, setConfig] = useState<ConfigBonificacao | null>(null);
  const [descricao, setDescricao] = useState("");
  const [tipo, setTipo] = useState<TipoCalculoBonificacao>("multiplo");
  const [multiplicador, setMultiplicador] = useState("1");
  const [valorFixo, setValorFixo] = useState("");
  const [parcelas, setParcelas] = useState("1");
  const [primeira, setPrimeira] = useState(proximoMes());
  const [anoMeta, setAnoMeta] = useState(String(new Date().getFullYear()));
  const [adiantamento, setAdiantamento] = useState(false);
  const [dataCorte, setDataCorte] = useState("");
  const [criando, setCriando] = useState(false);

  useEffect(() => {
    if (!novaOpen || !ehRH) return;
    api
      .get<ConfigBonificacao>("/bonificacoes/config")
      .then(setConfig)
      .catch(() => setConfig(null));
  }, [novaOpen, ehRH]);

  const numeroParcelas = Number(parcelas);
  const competencias = useMemo(
    () =>
      Number.isInteger(numeroParcelas) && numeroParcelas >= 1 && numeroParcelas <= 24 && /^\d{4}-\d{2}$/.test(primeira)
        ? Array.from({ length: numeroParcelas }, (_, i) => somarMeses(primeira, i))
        : [],
    [numeroParcelas, primeira],
  );

  const abrirNova = () => {
    setDescricao("");
    setTipo("multiplo");
    setMultiplicador("1");
    setValorFixo("");
    setParcelas("1");
    setPrimeira(proximoMes());
    setAnoMeta(String(new Date().getFullYear()));
    setAdiantamento(false);
    setDataCorte("");
    setNovaOpen(true);
  };

  const criar = async () => {
    const mult = Number(multiplicador.replace(",", "."));
    if (tipo === "multiplo" && !(mult > 0)) return void toast.error("Informe quantas vezes o salário");
    let fixo = 0;
    if (tipo === "fixo") {
      try {
        fixo = parseReaisInput(valorFixo);
      } catch {
        return void toast.error("Valor inválido");
      }
      if (fixo <= 0) return void toast.error("Informe o valor da bonificação");
    }
    if (competencias.length === 0) return void toast.error("Informe de 1 a 24 parcelas e a primeira competência");
    const ano = Number(anoMeta);
    if (!Number.isInteger(ano) || ano < 2000 || ano > 2100) return void toast.error("Informe o ano da meta");
    if (primeira < `${ano}-01`) return void toast.error("A primeira parcela não pode ser antes do ano da meta");
    const corte = dataCorte || fimDoMesAnterior(primeira);
    if (adiantamento && !corte.startsWith(`${ano}-`)) {
      return void toast.error("A data de corte do adiantamento tem que ser no ano da meta");
    }

    setCriando(true);
    try {
      const criada = await api.post<Bonificacao>("/bonificacoes", {
        ...(descricao.trim() ? { descricao: descricao.trim() } : {}),
        tipoCalculo: tipo,
        ...(tipo === "multiplo" ? { multiplicador: mult } : { valorFixo: fixo }),
        numeroParcelas,
        primeiraCompetencia: primeira,
        anoMeta: ano,
        ...(adiantamento && { adiantamento: true, dataCorte: corte }),
      });
      toast.success(`${nome} criada em ${criada.competencias.length} competência(s)`);
      setNovaOpen(false);
      nav(`/rh/bonificacao/${criada.competencias[0].id}`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Erro ao criar");
    } finally {
      setCriando(false);
    }
  };

  // Em andamento = alguma parcela ainda aberta; as concluídas vão para a tabela.
  const emAndamento = items.filter((b) => statusPrograma(b.competencias) !== "concluida");
  const anteriores = items.filter((b) => statusPrograma(b.competencias) === "concluida");

  return (
    <RoleGuard role={["rh", "financeiro"]}>
      <AppLayout title={nome}>
        <div className="mx-auto flex max-w-[1280px] flex-col gap-4 tabular-nums">
          <PageHeader
            titulo={nome}
            subtitulo="Uma bonificação por ano da meta: defina a regra, acompanhe as parcelas e feche. O PJ emite uma NF de bonificação com o valor de cada parcela, liberada quando o RH fecha a competência."
            acoes={
              ehRH && (
                <Button onClick={abrirNova}>
                  <Plus />
                  Nova bonificação
                </Button>
              )
            }
          />

          {carregando && items.length === 0 ? (
            <div className="flex justify-center py-16">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : items.length === 0 ? (
            <div className="rounded-xl border bg-card py-12 text-center text-sm text-muted-foreground">
              Nenhuma bonificação criada ainda.
            </div>
          ) : (
            <>
              {emAndamento.map((b) => (
                <CardPrograma key={b.id} b={b} nome={nome} />
              ))}

              <SecaoCard titulo={emAndamento.length > 0 ? "Bonificações anteriores" : "Bonificações"} semPadding>
                <Table className="min-w-[860px]">
                  <TableHeader>
                    <TableRow>
                      <TableHead className="pl-5 sm:pl-6">Bonificação</TableHead>
                      <TableHead className="w-[130px]">Status</TableHead>
                      <TableHead>Regra</TableHead>
                      <TableHead className="w-[130px] text-right">Colaboradores</TableHead>
                      <TableHead className="w-[170px] text-right">Total pago</TableHead>
                      <TableHead className="w-[150px]">Concluída em</TableHead>
                      <TableHead className="w-8" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {anteriores.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} className="py-10 text-center text-muted-foreground">
                          Nenhuma bonificação concluída ainda.
                        </TableCell>
                      </TableRow>
                    ) : (
                      anteriores.map((b) => {
                        const r = resumirPrograma(b.competencias);
                        const ultima = b.competencias[b.competencias.length - 1];
                        return (
                          <TableRow
                            key={b.id}
                            className="cursor-pointer"
                            onClick={() => ultima && nav(`/rh/bonificacao/${ultima.id}`)}
                          >
                            <TableCell className="pl-5 font-medium sm:pl-6">{tituloBonificacao(b, nome)}</TableCell>
                            <TableCell>
                              <span className="inline-flex items-center rounded-full bg-foreground px-2.5 py-0.5 text-xs font-semibold text-background">
                                Fechada
                              </span>
                            </TableCell>
                            <TableCell className="text-muted-foreground">{regraCurta(b)}</TableCell>
                            <TableCell className="text-right">{r.colaboradores}</TableCell>
                            <TableCell className="text-right font-semibold">{formatCents(r.pago)}</TableCell>
                            <TableCell className="text-muted-foreground">
                              {r.concluidaEm ? formatDate(r.concluidaEm) : "—"}
                            </TableCell>
                            <TableCell className="pl-0 pr-3">
                              <ChevronRight className="h-4 w-4 text-muted-foreground" />
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </SecaoCard>
            </>
          )}
        </div>
      </AppLayout>

      <Dialog open={novaOpen} onOpenChange={setNovaOpen}>
        <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Nova bonificação</DialogTitle>
            <DialogDescription>
              Cada parcela vira uma competência — o mês em que o PJ emite a NF de bonificação. Só PJ ativos com salário
              fixo entram.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Quanto cada um recebe</Label>
              <RadioGroup value={tipo} onValueChange={(v) => setTipo(v as TipoCalculoBonificacao)}>
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="multiplo" id="bon-multiplo" />
                  <Label htmlFor="bon-multiplo" className="font-normal">
                    Múltiplo do salário fixo
                  </Label>
                  {tipo === "multiplo" && (
                    <span className="flex items-center gap-1">
                      <Input
                        value={multiplicador}
                        onChange={(e) => setMultiplicador(e.target.value.replace(/[^\d,.]/g, ""))}
                        inputMode="decimal"
                        className="h-8 w-16"
                        aria-label="Vezes o salário"
                      />
                      <span className="text-sm text-muted-foreground">x</span>
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="fixo" id="bon-fixo" />
                  <Label htmlFor="bon-fixo" className="font-normal">
                    Valor fixo para todos
                  </Label>
                  {tipo === "fixo" && (
                    <Input
                      value={valorFixo}
                      onChange={(e) => setValorFixo(maskReaisInput(e.target.value))}
                      inputMode="numeric"
                      placeholder="R$ 0,00"
                      className="h-8 w-36"
                      aria-label="Valor para todos"
                    />
                  )}
                </div>
              </RadioGroup>
              <p className="text-xs text-muted-foreground">
                {tipo === "multiplo"
                  ? "Só o salário fixo, sem o complemento. Ex.: 2x com salário de R$ 1.000,00 = R$ 2.000,00."
                  : "Todos recebem o mesmo valor, independente do salário."}{" "}
                O valor é dividido entre as parcelas.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="bon-parcelas">Parcelas</Label>
                <Input
                  id="bon-parcelas"
                  type="number"
                  min={1}
                  max={24}
                  value={parcelas}
                  onChange={(e) => setParcelas(e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="bon-primeira">Emissão da 1ª NF</Label>
                <Input
                  id="bon-primeira"
                  type="month"
                  value={primeira}
                  onChange={(e) => setPrimeira(e.target.value)}
                />
              </div>
            </div>
            {competencias.length > 0 && (
              <p className="-mt-2 text-xs text-muted-foreground">
                Competências: {competencias.map((c) => formatMesRef(c)).join(", ")}
              </p>
            )}

            <div className="space-y-1.5">
              <Label htmlFor="bon-ano">Ano da meta</Label>
              <Input
                id="bon-ano"
                type="number"
                min={2000}
                max={2100}
                value={anoMeta}
                onChange={(e) => setAnoMeta(e.target.value)}
                className="w-28"
              />
              <p className="text-xs text-muted-foreground">
                {adiantamento
                  ? "O adiantamento é descontado da bonificação final deste ano."
                  : `Os dias trabalhados contam até 31/12/${anoMeta || "—"}, mesmo que a bonificação seja paga no ano seguinte. A janela de comissões também termina aí. Adiantamentos já fechados deste ano são descontados.`}
              </p>
            </div>

            <div className="space-y-3 rounded-md border p-3">
              <div className="flex items-center justify-between gap-4">
                <div className="space-y-0.5">
                  <Label htmlFor="bon-adiantamento">Adiantamento</Label>
                  <p className="text-xs text-muted-foreground">
                    Paga antes de fechar o ano da meta. O valor é calculado com as mesmas regras (use um múltiplo
                    menor, ex.: 0,5x) e descontado da bonificação final do ano.
                  </p>
                </div>
                <Switch id="bon-adiantamento" checked={adiantamento} onCheckedChange={setAdiantamento} />
              </div>
              {adiantamento && (
                <div className="space-y-1.5">
                  <Label htmlFor="bon-corte">Contar dias trabalhados até</Label>
                  <Input
                    id="bon-corte"
                    type="date"
                    value={dataCorte || fimDoMesAnterior(primeira)}
                    onChange={(e) => setDataCorte(e.target.value)}
                    className="w-44"
                  />
                  <p className="text-xs text-muted-foreground">
                    Data dentro do ano da meta. Quem ainda está em experiência nessa data não recebe o adiantamento.
                  </p>
                </div>
              )}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="bon-descricao">Descrição (opcional)</Label>
              <Input
                id="bon-descricao"
                value={descricao}
                onChange={(e) => setDescricao(e.target.value)}
                maxLength={200}
                placeholder="Ex.: Metas 2026"
              />
            </div>

            {config && (
              <p className="rounded-md border bg-muted/30 p-3 text-xs text-muted-foreground">
                Regras copiadas de Configurações: {descreverRegras(config)}.{" "}
                <Link to="/rh/configuracoes" className="underline">
                  Alterar
                </Link>
              </p>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setNovaOpen(false)} disabled={criando}>
              Cancelar
            </Button>
            <Button onClick={() => void criar()} disabled={criando}>
              {criando && <Loader2 className="animate-spin" />}
              Criar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </RoleGuard>
  );
};

export default RHBonificacoes;
